interface GoogleMapsWindow extends Window {
  google?: typeof google;
  initGoogleMapsApiCallback?: () => void; // Changed for clarity and to match usage
}

let loadingPromise: Promise<void> | null = null;
const SCRIPT_ID = 'google-maps-api-script'; // Define a unique ID for the script

export const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
  const customWindow = window as GoogleMapsWindow;

  // Check if script tag with our ID already exists or if API is already loaded
  if (document.getElementById(SCRIPT_ID) || (customWindow.google && customWindow.google.maps)) {
    if (customWindow.google && customWindow.google.maps && customWindow.google.maps.places) {
      // Fully loaded and ready
      return Promise.resolve();
    }
    // Script tag might exist but API not yet ready, or loadingPromise is set
    if (loadingPromise) {
      return loadingPromise; // Currently loading
    }
    // If script tag exists but API not ready and no loadingPromise, it might be a broken state
    // or loaded by another means. For now, we'll assume if our script tag is there,
    // our callback will handle it or it's already loaded.
    // If only window.google.maps exists but not .places, it might be loading without places lib.
    // This case is complex; the current check for .places should handle it.
  }

  if (loadingPromise) {
    // Script is already being loaded by this loader instance
    return loadingPromise;
  }

  loadingPromise = new Promise<void>((resolve, reject) => {
    if (!apiKey) {
      console.error('Google Maps API key is missing for script loader.');
      reject(new Error('Google Maps API key is missing.'));
      loadingPromise = null; 
      return;
    }

    // Double check again if script tag was added by a concurrent call before this promise body executed
    if (document.getElementById(SCRIPT_ID)) {
        // This can happen in some race conditions or with strict mode double invokes
        // If it's already there, and we have a loadingPromise, we should be fine.
        // If window.google.maps.places is now available, resolve.
        if (isGoogleMapsApiLoaded()) {
            resolve();
            loadingPromise = null;
            return;
        }
        // If it's there but not loaded, and loadingPromise is set, the existing promise will handle it.
        // This specific check inside the promise might be redundant if the outer checks are solid.
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID; // Assign an ID
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMapsApiCallback`;
    script.async = true;
    script.defer = true;

    // Define the global callback function
    customWindow.initGoogleMapsApiCallback = () => {
      console.log('Google Maps JavaScript API loaded successfully.');
      resolve();
      loadingPromise = null; // Clear promise on successful load
      delete customWindow.initGoogleMapsApiCallback; // Clean up callback
    };

    script.onerror = (error) => {
      console.error('Error loading Google Maps JavaScript API:', error);
      reject(new Error('Failed to load Google Maps script.'));
      loadingPromise = null; // Clear promise on error
      delete customWindow.initGoogleMapsApiCallback; // Clean up callback
    };

    document.head.appendChild(script);
  });

  return loadingPromise;
};

// It's good practice to also export a function to check if the API is loaded
export const isGoogleMapsApiLoaded = (): boolean => {
  const customWindow = window as GoogleMapsWindow;
  return !!(customWindow.google && customWindow.google.maps && customWindow.google.maps.places);
};
